import {cloneElement, useCallback} from "react";
import type {ComponentType, ReactElement} from "react";

import {isReactElement} from "../../react";
import {Button, ButtonIcon, ButtonSpinner, ButtonText, type IButtonProps} from "../ui/button";
import type {IIConProps} from "../ui/icon";
import type {IMenuProps} from "../ui/menu";
import {Pressable} from "../ui/pressable";
import {Tooltip} from "../ui/tooltip";

export function useDefaultTrigger(
    icon: ReactElement<IIConProps>,
    useButton: boolean
): IMenuProps["trigger"];
export function useDefaultTrigger(
    icon: ComponentType<IIConProps>,
    useButton: false,
    buttonProps: {
        variant?: IButtonProps["variant"];
        action?: IButtonProps["action"];
        text?: string;
        isLoading?: boolean;
    },
    tooltip?: string
): IMenuProps["trigger"];
export function useDefaultTrigger(
    icon: ReactElement<IIConProps>,
    useButton: false,
    buttonProps?: undefined,
    tooltip?: string
): IMenuProps["trigger"];
export function useDefaultTrigger(
    icon: ReactElement<IIConProps> | ComponentType<IIConProps>,
    useButton: boolean,
    buttonProps?: {
        variant?: IButtonProps["variant"];
        action?: IButtonProps["action"];
        text?: string;
        isLoading?: boolean;
    },
    tooltip?: string
) {
    return useCallback<IMenuProps["trigger"]>(
        props => {
            const node = useButton ? (
                <Pressable accessibilityLabel="menu" testID="menu-trigger" {...props}>
                    {isReactElement(icon) ? cloneElement(icon, props) : null}
                </Pressable>
            ) : buttonProps ? (
                <Button testID="menu-trigger" variant="ghost" {...buttonProps} {...props}>
                    {buttonProps.isLoading ? (
                        <ButtonSpinner />
                    ) : !isReactElement(icon) ? (
                        <ButtonIcon as={icon} />
                    ) : null}
                    <ButtonText>{buttonProps?.text}</ButtonText>
                </Button>
            ) : (
                <Pressable accessibilityLabel="menu" testID="menu-trigger" {...props}>
                    {icon}
                </Pressable>
            );

            if (tooltip) {
                return <Tooltip label={tooltip}>{node}</Tooltip>;
            }

            return node;
        },
        [icon, buttonProps, useButton, tooltip]
    );
}
